import { Content } from '@backstage/core-components';
import { PageShareTitle } from '../../lib/shareTitle';
import { EntityContentBlueprint } from '@backstage/plugin-catalog-react/alpha';
import { getStoredFunMode, translate } from '../i18n';

/**
 * `usvc-migrations` is itself a catalog Component of `spec.type: migration`
 * (not `service`) — this filter keys both tabs below off that type so they
 * only show up on that one entity, the same way the Kafka topic tab keys off
 * `spec.type: kafka-topic` (see `TopicKafkaContent.tsx`).
 */
const MIGRATION_FILTER = {
  'spec.type': 'migration',
};

/**
 * The "Runs" tab in the native "Migrations" catalog group — a read-only
 * observability dashboard (env switcher + filters + runs table + live log
 * tail), all backed by `usvc-migrations-backend`. Sibling of
 * `runMigrationUsvcMigrationsContent`; together they form the "Migrations"
 * group (see `page:catalog/entity`'s `groups` in app-config.yaml), the same
 * way the `configuration` group folds Resources/Environment/Secret under one
 * top-level tab with a dropdown.
 * Extension id: entity-content:usvc-migrations/runs.
 */
export const runsUsvcMigrationsContent = EntityContentBlueprint.make({
  name: 'runs',
  params: {
    path: '/usvc-migrations',
    // Non-component config (evaluated once at extension registration), so
    // this reads the fun-mode flag directly instead of the useT() hook — see
    // the i18n module's non-component-code note.
    title: translate('usvcMigrations.tab.runs', getStoredFunMode()),
    group: 'migrations',
    filter: MIGRATION_FILTER,
    loader: () =>
      import('./RunsCard').then(m => (
        <PageShareTitle title="USVC migrations">
          <Content>
            <m.RunsCard />
          </Content>
        </PageShareTitle>
      )),
  },
});

/**
 * The "Run migration" tab in the native "Migrations" catalog group — the
 * on-demand trigger form (`RunMigrationPanel`) that dispatches the
 * `run-usvc-migration` scaffolder template, gated by an approval on
 * pre-prod/production.
 * Extension id: entity-content:usvc-migrations/run-migration.
 */
export const runMigrationUsvcMigrationsContent = EntityContentBlueprint.make({
  name: 'run-migration',
  params: {
    path: '/usvc-migrations/run',
    title: translate('usvcMigrations.tab.runMigration', getStoredFunMode()),
    group: 'migrations',
    filter: MIGRATION_FILTER,
    loader: () =>
      import('./RunMigrationPanel').then(m => (
        <PageShareTitle title="Run USVC migration">
          <Content>
            <m.RunMigrationPanel />
          </Content>
        </PageShareTitle>
      )),
  },
});
